<template>
  <div class="relative" ref="dropdownRef">
    <!-- 36px, like every other control in the header (§3.5). `-right-1` -> `-start-1`: in RTL the
         right edge IS the start edge, so this is zero pixels and one less physical class for the
         next reader to decode (§8.1). The bell had no accessible name at all — the defect §5.1
         records for most of the panel's 31 icon-only buttons — and the count is now spoken as well
         as painted. -->
    <button
      type="button"
      class="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
      :aria-label="hasNewChanges ? 'کارهای منتظر رسیدگی؛ تغییر تازه دارد' : 'کارهای منتظر رسیدگی'"
      :title="hasNewChanges ? 'از آخرین مشاهده، موارد بیشتر یا مهم‌تر شده‌اند' : 'مشاهدهٔ کارهای منتظر رسیدگی'"
      :aria-expanded="dropdownOpen"
      @click="toggleDropdown"
    >
      <!-- unread badge -->
      <span
        v-if="hasNewChanges"
        aria-hidden="true"
        data-testid="attention-new-change"
        class="absolute start-1 top-1 h-2 w-2 rounded-full bg-brand-500 ring-2 ring-white dark:ring-gray-900"
      />
      <Bell class="h-5 w-5" />
    </button>

    <!-- Compact backlog summary; rows navigate to the work queue that resolves each item. -->
    <div
      v-if="dropdownOpen"
      class="absolute end-0 z-[1000] mt-2 flex max-h-80 w-[300px] max-w-[calc(100vw-1rem)] flex-col rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-800 dark:bg-gray-900"
    >
      <div class="flex items-center gap-1 border-b border-gray-100 px-1 pb-1.5 dark:border-gray-800">
        <h2 class="min-w-0 flex-1 text-sm font-semibold text-gray-800 dark:text-white/90">کارهای منتظر</h2>
        <span v-if="generatedAt" class="text-xs tabular-nums text-gray-500 dark:text-gray-400" :title="`آخرین بررسی: ${formatTime(generatedAt)}`">{{ formatTime(generatedAt) }}</span>
        <div class="flex items-center gap-0.5">
          <button type="button" class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-brand-600 dark:hover:bg-gray-800" title="به‌روزرسانیِ فهرستِ اعلان‌ها" aria-label="به‌روزرسانیِ فهرستِ اعلان‌ها" :aria-busy="loading ? 'true' : undefined" @click.stop="load">
            <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': loading }" />
          </button>
          <button type="button" class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="بستنِ اعلان‌ها" @click="closeDropdown">
            <X class="h-4 w-4" />
          </button>
        </div>
      </div>

      <span class="sr-only">عددها تعداد کارهای باقی‌مانده‌اند، نه اعلان‌های جدید.</span>
      <p v-if="loadError" role="alert" class="mb-2 px-1 text-sm text-amber-700 dark:text-amber-300">اطلاعات تازه دریافت نشد؛ {{ generatedAt ? 'آخرین وضعیت نمایش داده می‌شود.' : 'دوباره تلاش کنید.' }}</p>
      <p v-if="loading && !generatedAt" role="status" class="p-3 text-sm text-gray-500">در حال دریافت وضعیت…</p>
      <!-- Empty -->
      <div v-if="!allItems.length && !loading && !loadError" class="flex flex-col items-center gap-2 px-2 py-6 text-center">
        <span class="flex h-9 w-9 items-center justify-center rounded-full bg-success-50 text-success-600 dark:bg-success-900/20 dark:text-success-400">
          <CheckCircle2 class="h-5 w-5" />
        </span>
        <p class="text-sm text-gray-500 dark:text-gray-400">کاری منتظر رسیدگی نیست.</p>
      </div>

      <!-- Items -->
      <ul v-if="allItems.length" class="-mx-1 mt-1 flex flex-col overflow-y-auto px-1">
        <li v-for="it in allItems" :key="it.key">
          <button
            type="button"
            class="flex w-full items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 text-start transition hover:border-gray-200 hover:bg-gray-50 dark:hover:border-gray-700 dark:hover:bg-white/5"
            :aria-label="`${it.title}، ${toFa(it.count)} مورد${compactDetail(it) ? `؛ ${compactDetail(it)}` : ''}`"
            @click="go(it.link)"
          >
            <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" :class="toneBg(it.severity)">
              <component :is="iconFor(it.icon)" class="h-4 w-4" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-[13px] font-medium text-gray-800 dark:text-white/90" :title="it.title">{{ it.title }}</span>
              <span v-if="compactDetail(it)" class="block truncate text-xs text-gray-500 dark:text-gray-400" :title="compactDetail(it)">{{ compactDetail(it) }}</span>
            </span>
            <span class="shrink-0 rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums" :class="toneChip(it.severity)">{{ toFa(it.count) }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Bell, AlertTriangle, ShieldCheck, Flag, FileQuestion, ServerCrash, Trash2, CheckCircle2, RefreshCw, X, MessageSquare } from 'lucide-vue-next'
import api from '@/services/api'
import { usePermission } from '@/composables/usePermission'
import { useSupportAttention } from '@/composables/useSupportAttention'

const router = useRouter()
const dropdownOpen = ref(false)
const dropdownRef = ref(null)
const items = ref([])
const hasNewChanges = ref(false)
const loadError = ref(false)
let seenSnapshot = null
let disposed = false
const generatedAt = ref(null)
const loading = ref(false)
let pollTimer = null
const { can } = usePermission()
const { waitingForUs, overdue, unassigned, refresh: refreshSupport } = useSupportAttention()

const supportItem = computed(() => {
  if (!can('support.read') || waitingForUs.value < 1) return null
  const details = []
  if (overdue.value > 0) details.push(`${toFa(overdue.value)} دیرکرد`)
  if (unassigned.value > 0) details.push(`${toFa(unassigned.value)} بدون مسئول`)
  return {
    key: 'support-waiting',
    title: 'درخواست‌های پشتیبانی',
    detail: details.join('؛ '),
    count: waitingForUs.value,
    severity: overdue.value > 0 ? 'critical' : 'warning',
    icon: 'support',
    link: '/support',
  }
})
const allItems = computed(() => supportItem.value ? [supportItem.value, ...items.value] : items.value)
const compactDetail = item => item.key === 'review' ? '' : item.detail || ''
const snapshot = () => Object.fromEntries(allItems.value.map(item => [item.key, { count: Number(item.count), severity: item.severity }]))
const markSeen = () => { seenSnapshot = snapshot(); hasNewChanges.value = false }

const toFa = (n) => Number(n ?? 0).toLocaleString('fa-IR')
const formatTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }) : '')

const ICONS = { alert: AlertTriangle, approval: ShieldCheck, moderation: Flag, review: FileQuestion, job: ServerCrash, deletion: Trash2, support: MessageSquare }
const iconFor = (k) => ICONS[k] ?? Bell

/**
 * §4.3's ramp swap, and §4.1's meanings. `red-*` -> `error-*` and `amber-*` -> `warning-*` are pure
 * renames onto the tokens this panel measured for AA. `blue-*` is the one that changes meaning:
 * §4.3 retires it to `brand-*` when a thing is IN FLIGHT and to neutral when it is «merely
 * factual», and §4.1 forbids brand from ever carrying a quality or a status. An `info` notification
 * is a fact the operator may read, not a place the system is asking them to look, so it goes grey —
 * which also makes it visibly quieter than `warning`, which it previously matched in weight.
 *
 * The tone still arrives with an icon and a Persian sentence beside it (§4.5): the shape is the
 * channel a red-green deficient reviewer reads, and colour is the third cue rather than the only one.
 */
const toneBg = (s) => ({
  critical: 'bg-error-50 text-error-600 dark:bg-error-900/20 dark:text-error-400',
  warning: 'bg-warning-50 text-warning-600 dark:bg-warning-900/20 dark:text-warning-400',
  info: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300',
}[s] || 'bg-gray-100 text-gray-500')

const toneChip = (s) => ({
  critical: 'bg-error-100 text-error-700 dark:bg-error-900/40 dark:text-error-300',
  warning: 'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300',
  info: 'bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-gray-200',
}[s] || 'bg-gray-200 text-gray-600')

const load = async () => {
  if (loading.value) return
  loading.value = true
  try {
    const [{ data }] = await Promise.all([
      api.get('/notifications/feed'),
      can('support.read') ? refreshSupport({ force: true }) : Promise.resolve(),
    ])
    if (disposed) return
    const d = data?.data ?? {}
    items.value = d.items ?? []
    generatedAt.value = d.generated_at ?? null
    loadError.value = false
    if (seenSnapshot === null || dropdownOpen.value) markSeen()
    else {
      const rank = { info: 0, warning: 1, critical: 2 }
      hasNewChanges.value = allItems.value.some(item => {
        const seen = seenSnapshot[item.key]
        return !seen || Number(item.count) > seen.count || (rank[item.severity] ?? 0) > (rank[seen.severity] ?? 0)
      })
    }
  } catch (e) {
    // a transient failure shouldn't spam the UI; keep the last good state
    if (!disposed) loadError.value = true
  } finally {
    loading.value = false
  }
}

const go = (link) => {
  closeDropdown()
  if (link) router.push(link)
}

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value
  if (dropdownOpen.value) { if (seenSnapshot !== null) markSeen(); load() }
}
const closeDropdown = () => { dropdownOpen.value = false }

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) closeDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  load()
  pollTimer = setInterval(load, 60000) // refresh the backlog every minute
})

onUnmounted(() => {
  disposed = true
  document.removeEventListener('click', handleClickOutside)
  if (pollTimer) clearInterval(pollTimer)
})
</script>
